import React from "react";
import { Helmet } from "react-helmet";

export default function About() {
  return (
    <div className="font-nunito">
      <Helmet>
        <title>Who We Are | Pathway Resource Center</title>
        <meta
          name="description"
          content="Discover Pathway Resource Center's mission and goal for the community."
        />
      </Helmet>

      {/* Hero Quote Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#457B9D] via-[#5a8fb0] to-[#457B9D] py-20">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute left-0 top-0 h-96 w-96 rounded-full bg-white"></div>
          <div className="absolute bottom-0 right-0 h-80 w-80 rounded-full bg-white"></div>
        </div>

        <div className="relative mx-auto max-w-4xl px-6">
          <div className="rounded-3xl bg-white bg-opacity-95 p-8 shadow-2xl md:p-12">
            <svg
              className="mb-4 h-12 w-12 text-[#457B9D] opacity-50"
              fill="currentColor"
              viewBox="0 0 32 32"
            >
              <path d="M10 8c-3.3 0-6 2.7-6 6v8h6v-8h-4c0-2.2 1.8-4 4-4V8zm12 0c-3.3 0-6 2.7-6 6v8h6v-8h-4c0-2.2 1.8-4 4-4V8z" />
            </svg>
            <p className="mb-6 text-xl leading-relaxed text-gray-700 md:text-2xl">
              Life is full of choices that carry us down paths leading to different
              destinations. It is Pathway Resource Center's hope to make the better
              path more obvious by providing knowledge and understanding of choices
              and the consequences of those choices BEFORE they are made and the
              journey begins.
            </p>
            <p className="text-lg font-semibold text-[#457B9D]">
              — Pathway Board of Directors
            </p>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="bg-gradient-to-b from-white to-blue-50 py-16">
        <div className="mx-auto max-w-6xl px-6">
          <div className="mb-16 text-center">
            <h2 className="mb-4 text-4xl font-bold text-gray-800 md:text-5xl">
              Our Mission
            </h2>
            <div className="mx-auto h-1 w-24 rounded bg-[#457B9D]"></div>
          </div>

          <div className="rounded-3xl bg-white p-8 shadow-xl md:p-12">
            <p className="text-center text-xl leading-relaxed text-gray-700 md:text-2xl">
              Pathway's mission is to{" "}
              <span className="font-semibold text-[#457B9D]">
                share the love of Jesus Christ
              </span>{" "}
              within our community by offering{" "}
              <span className="font-semibold text-[#457B9D]">
                sexual risk avoidance and life affirming services
              </span>{" "}
              in order to prevent or alleviate a lifestyle crisis.
            </p>
          </div>
        </div>
      </section>

      {/* Statement of Faith Section */}
      <section className="bg-gradient-to-b from-blue-50 to-white py-16">
        <div className="mx-auto max-w-6xl px-6">
          <div className="mb-12 text-center">
            <h2 className="mb-4 text-4xl font-bold text-gray-800 md:text-5xl">
              Our Statement of Faith
            </h2>
            <div className="mx-auto h-1 w-24 rounded bg-[#457B9D]"></div>
            <p className="mt-6 text-lg text-gray-600">
              These are the foundational beliefs that guide our work and service
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-2">
            <div className="group rounded-2xl bg-white p-6 shadow-md transition-all duration-300 hover:shadow-xl">
              <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-blue-100 text-xl font-bold text-[#457B9D]">
                1
              </div>
              <p className="leading-relaxed text-gray-700">
                We believe the Bible to be the inspired, the only infallible,
                authoritative Word of God.
              </p>
            </div>

            <div className="group rounded-2xl bg-white p-6 shadow-md transition-all duration-300 hover:shadow-xl">
              <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-blue-100 text-xl font-bold text-[#457B9D]">
                2
              </div>
              <p className="leading-relaxed text-gray-700">
                We believe that there is one God, eternally existent in three
                persons: Father, Son, and the Holy Spirit.
              </p>
            </div>

            <div className="group rounded-2xl bg-white p-6 shadow-md transition-all duration-300 hover:shadow-xl">
              <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-blue-100 text-xl font-bold text-[#457B9D]">
                3
              </div>
              <p className="leading-relaxed text-gray-700">
                We believe in the deity of our Lord Jesus Christ, in His virgin
                birth, in His sinless life, in His miracles, in His vicarious and
                atoning death through His shed blood, in His bodily resurrection, in
                His ascension to the right hand of the Father, and in His personal
                return in power and glory.
              </p>
            </div>

            <div className="group rounded-2xl bg-white p-6 shadow-md transition-all duration-300 hover:shadow-xl">
              <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-blue-100 text-xl font-bold text-[#457B9D]">
                4
              </div>
              <p className="leading-relaxed text-gray-700">
                We believe that for the salvation of lost and sinful man,
                regeneration of the Holy Spirit is absolutely essential.
              </p>
            </div>

            <div className="group rounded-2xl bg-white p-6 shadow-md transition-all duration-300 hover:shadow-xl">
              <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-blue-100 text-xl font-bold text-[#457B9D]">
                5
              </div>
              <p className="leading-relaxed text-gray-700">
                We believe in the present ministry of the Holy Spirit by whose
                indwelling the Christian is enabled to live a godly life.
              </p>
            </div>

            <div className="group rounded-2xl bg-white p-6 shadow-md transition-all duration-300 hover:shadow-xl">
              <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-blue-100 text-xl font-bold text-[#457B9D]">
                6
              </div>
              <p className="leading-relaxed text-gray-700">
                We believe in the resurrection of both the saved and the lost; they
                that are saved unto the resurrection of life and they that are lost
                unto the resurrection of damnation.
              </p>
            </div>

            <div className="group col-span-full rounded-2xl bg-gradient-to-r from-[#457B9D] to-[#5a8fb0] p-6 shadow-md transition-all duration-300 hover:shadow-xl">
              <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-white text-xl font-bold text-[#457B9D]">
                7
              </div>
              <p className="leading-relaxed text-white">
                We believe in the spiritual unity of believers in our Lord Jesus
                Christ.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="bg-gradient-to-r from-pink-50 to-blue-50 py-16">
        <div className="mx-auto max-w-4xl px-6 text-center">
          <h2 className="mb-6 text-3xl font-bold text-gray-800 md:text-4xl">
            Want to Learn More or Get Involved?
          </h2>
          <p className="mb-8 text-xl text-gray-600">
            We'd love to connect with you and share more about our work in the
            community.
          </p>
          <div className="flex flex-col gap-4 sm:flex-row sm:justify-center">
            <a
              href="/contact"
              className="rounded-xl bg-[#457B9D] px-8 py-4 text-lg font-semibold text-white shadow-lg transition-all duration-300 hover:bg-[#365f7a] hover:shadow-xl"
            >
              Contact Us
            </a>
            <a
              href="/get-involved"
              className="rounded-xl border-2 border-[#457B9D] bg-white px-8 py-4 text-lg font-semibold text-[#457B9D] shadow-lg transition-all duration-300 hover:bg-[#457B9D] hover:text-white hover:shadow-xl"
            >
              Get Involved
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
